import React, {useState, useEffect, useCallback} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {$$} from '../../helpers/localization'
import {notificationsService} from '../../service/notifications_service'
import {fetchNotificationUnreadCount} from '../../actions/notifications_actions'
import {NotificationItem} from './NotificationItem'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'

const PAGE_SIZE = 20;

export default function NotificationsPage() {
    const dispatch = useDispatch();
    useSelector(state => state.language);

    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('NOT_SEEN');
    const [expandedId, setExpandedId] = useState(null);
    const [emailOptedIn, setEmailOptedIn] = useState(null);
    const [emailSaving, setEmailSaving] = useState(false);

    const loadInbox = useCallback((offset, currentFilter) => {
        setLoading(true);
        const params = {offset, limit: PAGE_SIZE};
        if (currentFilter !== 'ALL') {
            params.filter = currentFilter;
        }
        notificationsService.getInbox(params)
            .then(res => {
                if (offset === 0) {
                    setItems(res.items);
                } else {
                    setItems(prev => [...prev, ...res.items]);
                }
                setHasMore(res.hasMore);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setExpandedId(null);
        loadInbox(0, filter);
        dispatch(fetchNotificationUnreadCount());
    }, [filter, loadInbox, dispatch]);

    useEffect(() => {
        notificationsService.getPreferences()
            .then(res => setEmailOptedIn(!res.announcementsOptedOut))
            .catch(() => {});
    }, []);

    const handleLoadMore = () => {
        loadInbox(items.length, filter);
    };

    const handleMarkSeen = useCallback((campaignId) => {
        notificationsService.markSeen(campaignId)
            .then(() => {
                setItems(prev => prev.map(item =>
                    item.campaignId === campaignId
                        ? {...item, seenAt: new Date().toISOString()}
                        : item
                ));
                dispatch(fetchNotificationUnreadCount());
            })
            .catch(() => {});
    }, [dispatch]);

    const handleToggleEmail = (e) => {
        const nextOptedIn = e.target.checked;
        const previousOptedIn = emailOptedIn;
        setEmailOptedIn(nextOptedIn);
        setEmailSaving(true);
        notificationsService.updatePreferences({announcementsOptedOut: !nextOptedIn})
            .then(res => setEmailOptedIn(!res.announcementsOptedOut))
            .catch(() => setEmailOptedIn(previousOptedIn))
            .finally(() => setEmailSaving(false));
    };

    return (
        <div className="wrapping-div">
            <h2>{$$('notification_inbox')}</h2>
            <div className="mt-3 mb-3 d-flex align-items-center justify-content-between flex-wrap">
                <ButtonGroup style={{display: 'inline-flex'}}>
                    <Button
                        variant={filter === 'NOT_SEEN' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setFilter('NOT_SEEN')}
                    >
                        {$$('unread_notifications_filter')}
                    </Button>
                    <Button
                        variant={filter === 'ALL' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setFilter('ALL')}
                    >
                        {$$('all_label')}
                    </Button>
                </ButtonGroup>
                {emailOptedIn !== null && (
                    <div className="custom-control custom-checkbox mb-0 pointer">
                        <input
                            type="checkbox"
                            id="email_notifications_optin"
                            className="custom-control-input"
                            checked={emailOptedIn}
                            disabled={emailSaving}
                            onChange={handleToggleEmail}
                        />
                        <label className="custom-control-label" htmlFor="email_notifications_optin">
                            {$$('receive_email_notifications')}
                        </label>
                    </div>
                )}
            </div>
            <div>
                {items.length === 0 && !loading && (
                    <div className="high-shadow-container p-4">
                        <p className="medrec-grey-2 mb-0">{$$('no_notifications')}</p>
                    </div>
                )}
                {items.length > 0 && items.map(item => (
                    <NotificationItem
                        key={item.campaignId}
                        item={item}
                        expanded={expandedId === item.campaignId}
                        onToggle={(id) => setExpandedId(prev => prev === id ? null : id)}
                        onMarkSeen={handleMarkSeen}
                    />
                ))}
                {hasMore && (
                    <div className="text-center mt-3">
                        <Button
                            variant="outline-primary"
                            onClick={handleLoadMore}
                            disabled={loading}
                        >
                            {$$('load_more')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
